<template>
  <CardHitosPago
    :viaje="viaje"
    :selected-pasarela-pago="selectedPasarelaPago"
    :precio-total="precioTotalGrupo"
    :total-pagado="totalPagadoGrupo"
    :hitos-pago-next="hitosPagoNext"
    :hitos-pago-past="hitosPagoPast"
    :is-pago-unico-forced="isPagoUnicoForced"
    :is-pagos-sucesivos="isPagosSucesivos"
    :fecha-vencimiento-hito-actual-already-payed="fechaVencimientoHitoActualAlreadyPayed"
    @modo-pago="handleModoPagoChanged"
    @total-pendiente="handleTotalPendienteChanged" />
</template>

<script setup>
  import { ref, computed, nextTick, watch, onBeforeMount } from 'vue';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import currency from 'currency.js';
  import CardHitosPago from '@/components/card/viaje/CardHitosPago.vue';
  import { calculaHitosPago, calcularTotalPagado } from '@/helpers/hitosPagoUtils';
  import { TIPOS_PAGO } from '@/constants/viajeConstants';

  const props = defineProps({
    isPagosSucesivos: {
      type: Boolean,
      required: false,
      default: false,
    },
    precioTotalGrupo: {
      type: Number,
      required: true,
    },
    selectedPasarelaPago: {
      type: Object,
      required: true,
    },
    reservas: {
      type: Array,
      required: false,
      default: null,
    },
  });

  const emits = defineEmits(['modo-pago', 'precio-calendarizado', 'total-pendiente']);

  const storeHabitacion = informacionHabitacion();
  const modoPagoSelected = ref(TIPOS_PAGO.ALL);
  const fechaVencimientoHitoActualAlreadyPayed = ref(null);

  const viaje = computed(() => storeHabitacion.getViaje);
  const calculatedReservas = computed(() => props.reservas ?? storeHabitacion.getResumenReservas);
  const isPagoUnicoForced = computed(
    () =>
      storeHabitacion.getCodigoDescuento?.pagoUnico ||
      !viaje.value?.pagoByCalendarioAllowed ||
      (storeHabitacion.getAlojamiento && storeHabitacion.getAlojamiento.habitacion.integrado)
  );

  const totalPagadoGrupo = computed(() =>
    calculatedReservas.value?.reduce((accum, reserva) => accum.add(calcularTotalPagado(reserva)), currency(0))
  );

  const hitosPago = computed(() => {
    const result = calculaHitosPago(
      viaje.value?.hitosPago,
      calculatedReservas.value,
      viaje.value
    );
    fechaVencimientoHitoActualAlreadyPayed.value = result.fechaVencimientoHitoActual;
    return result;
  });

  const hitosPagoNext = computed(() => hitosPago.value.pendientes);
  const hitosPagoPast = computed(() => hitosPago.value.pasados);

  function handleModoPagoChanged(selectedValue) {
    modoPagoSelected.value = selectedValue;
    emits('modo-pago', selectedValue);
    emits(
      'precio-calendarizado',
      (selectedValue === TIPOS_PAGO.PAST ? hitosPagoPast.value : hitosPagoNext.value)[0]?.cantidadPendiente
    );
  }

  function handleTotalPendienteChanged(totalPendiente) {
    emits('total-pendiente', totalPendiente);
  }

  watch(hitosPagoNext, () => {
    emits(
      'precio-calendarizado',
      (modoPagoSelected.value === TIPOS_PAGO.PAST ? hitosPagoPast.value : hitosPagoNext.value)[0]?.cantidadPendiente
    );
  });

  onBeforeMount(async () => {
    await nextTick();
    emits('modo-pago', modoPagoSelected.value);
    emits('precio-calendarizado', hitosPagoNext.value[0]?.cantidadPendiente);
  });
</script>
